import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing8, Spacing24 } from 'autogen/design-tokens/tokens';
import { AddMultipleToBasket, type AddMultipleToBasketParams } from 'autogen/swagger/all';
import * as transKey from 'autogen/translation-keys/trans-root-website';
import { ButtonWithSpinner } from 'components/Button';
import PriceModule from 'components/generic/PriceModule';
import { Body } from 'components/generic/typography';
import { REVIEVE_PRODUCTS_TRACKING_SESSION_KEY } from 'components/product/RevieveVirtualTryOn';
import { refreshBasketQuantityGlobally } from 'hooks/basket/useBasketQuantity';
import { useSessionState } from 'hooks/storage';
import mediator from 'modules/mediator';
import track from 'modules/tracking/dataLayer';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { breakpoint, sizes } from 'variables';
import { useRegime } from './Regime';

async function subsequentlyAddToBasket(productIds): Promise<void> {
    const { subsequentlyAddToBasket: subsubsequentlyAddToBasketMethod } = await import(
        /* webpackChunkName: "addAndRemoveFromBasket" */ 'modules/addAndRemoveFromBasket'
    );

    subsubsequentlyAddToBasketMethod(productIds);
}

/* #region  styles */
const StyledButton = styled(ButtonWithSpinner)`
    justify-self: center;
    width: 260px;
`;

const Container = styled.div`
    margin-top: ${Spacing24};
    display: grid;
    justify-content: center;
    grid-column: 1;
    gap: ${Spacing8};

    ${breakpoint.up(sizes.sm)} {
        justify-content: center;
    }
`;

const StyledPriceModule = styled(PriceModule)`
    && {
        justify-self: center;
    }
`;
/* #endregion */

export default function RegimePriceButton({ ...restProps }) {
    const { regimeModel, getSelectedProducts, hasSelectedProducts, mainProductRegimeId } = useRegime();
    const { earnXPointsText, addXToBasketText, priceBeforeText, priceText, onDiscount } = regimeModel;
    const { t } = useTranslation();
    const [revieveProducts] = useSessionState<number[]>(REVIEVE_PRODUCTS_TRACKING_SESSION_KEY, []);

    const hasProductsSelected = hasSelectedProducts();

    const addToBasket = async () => {
        const selectedProducts = getSelectedProducts();

        // Find selected products that used Revieve (virtual try-on) in this session
        const productsThatUsedRevieve = selectedProducts.filter((productId) => revieveProducts.includes(productId));

        const params: AddMultipleToBasketParams = {
            productIds: selectedProducts,
            bundleRelation: mainProductRegimeId.toString(),
            bundleType: 'Regime',
            usedRevieve: productsThatUsedRevieve
        };

        await AddMultipleToBasket(params).then((response) => {
            refreshBasketQuantityGlobally();
            mediator.publish('updateMiniBasketView');
            subsequentlyAddToBasket(selectedProducts);
            track(response.dataLayerDto);

            GA4Tracking({
                eventName: 'add_to_cart',
                context: 'product_page',
                category: 'ecommerce',
                eventParams: [
                    {
                        param_name: 'is_regime',
                        param_value: true
                    },
                    {
                        param_name: 'add_to_cart_quantity',
                        param_value: selectedProducts.length
                    }
                ]
            });
        });
    };

    return (
        <Container {...restProps}>
            {priceText ? (
                <StyledPriceModule
                    price={priceText}
                    priceLabel={t(transKey.ProductRegime.PriceTotal) as any}
                    priceBefore={priceBeforeText}
                    isOnDiscount={onDiscount}
                    size="sm"
                />
            ) : null}

            <StyledButton disabled={!hasProductsSelected} onClick={addToBasket}>
                {hasProductsSelected ? addXToBasketText : t(transKey.ProductRegime.NoProductsSelected)}
            </StyledButton>

            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
            <Body size="M" dangerouslySetInnerHTML={{ __html: earnXPointsText }} />
        </Container>
    );
}
